/**
 * Admin Compliance dashboard — SOC 2 readiness surface.
 * 
 * Three panels:
 *   1. Posture summary + Download Security & Compliance Brief PDF
 *   2. SOC 2 control checklist (status badges) + sub-processor inventory
 *   3. Unified audit log across copilot / passport / hazard sources
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { SiteHeader } from '../../components/SiteLayout';
import {
  ShieldCheck, FilePdf, ListChecks, Database, Lock, Users, ArrowsClockwise,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_THEME = {
  implemented:   { bg: '#ECFDF5', fg: '#065F46', label: 'IMPLEMENTED' },
  partial:       { bg: '#FFFBEB', fg: '#92400E', label: 'PARTIAL' },
  planned:       { bg: '#EFF6FF', fg: '#1E40AF', label: 'PLANNED' },
  out_of_scope:  { bg: '#F3F4F6', fg: '#6B7280', label: 'OUT OF SCOPE' },
};

export default function CompliancePage() {
  const [summary, setSummary] = useState(null);
  const [controls, setControls] = useState([]);
  const [subProcessors, setSubProcessors] = useState([]);
  const [auditRows, setAuditRows] = useState([]);
  const [sourceFilter, setSourceFilter] = useState('');
  const [generating, setGenerating] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [sm, ct, sp] = await Promise.all([
        axios.get(`${API_URL}/api/compliance/summary`, { withCredentials: true }),
        axios.get(`${API_URL}/api/compliance/controls`, { withCredentials: true }),
        axios.get(`${API_URL}/api/compliance/sub-processors`, { withCredentials: true }),
      ]);
      setSummary(sm.data);
      setControls(ct.data?.controls || []);
      setSubProcessors(sp.data?.sub_processors || []);
    } catch (e) { toast.error('Could not load compliance state — admin only'); }
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      const params = sourceFilter ? `?source=${sourceFilter}` : '';
      const r = await axios.get(`${API_URL}/api/compliance/audit-log${params}`, { withCredentials: true });
      setAuditRows(r.data?.rows || []);
    } catch (e) {
      // Audit log is optional — non-admins or empty logs render an empty table.
      console.warn('Audit log load failed:', e);
    }
  }, [sourceFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadAudit(); }, [loadAudit]);

  const downloadBrief = () => {
    setGenerating(true);
    const a = document.createElement('a');
    a.href = `${API_URL}/api/compliance/brief.pdf`;
    a.download = 'ToxIntel_Security_Compliance_Brief.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success('Brief downloaded · share under NDA only');
    setTimeout(() => setGenerating(false), 1500);
  };

  return (
    <div className="min-h-screen bg-[#F5F6F8]" data-testid="compliance-page">
      <SiteHeader />

      {/* Hero */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-7">
          <div className="flex items-baseline gap-3 mb-2">
            <ShieldCheck size={20} className="text-[#E63946]" weight="duotone" />
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#4B5563]">
              ToxIntel by Aᴳ · Security &amp; Compliance
            </p>
          </div>
          <h1 className="text-2xl font-bold text-[#0B2447] tracking-tight" style={{ fontFamily: 'Georgia, serif' }}>
            SOC 2 readiness surface
          </h1>
          <p className="text-[12px] text-[#6B7280] mt-1 max-w-2xl">
            {summary?.framework || 'AICPA SOC 2 (2017 TSC, revised)'} ·
            scope: <span className="font-bold text-[#0B2447]">{summary?.scope || 'Security (CC), Confidentiality (C)'}</span> ·
            last updated {summary?.last_updated || '—'}
          </p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Posture summary */}
        <section data-testid="posture-summary" className="grid md:grid-cols-4 gap-4">
          <PostureTile icon={ListChecks} label="Controls" value={summary?.controls_total ?? '—'}
            sub={summary && Object.entries(summary.controls_by_status || {}).map(([k, v]) => `${v} ${k}`).join(' · ')} />
          <PostureTile icon={Database} label="Sub-processors" value={summary?.sub_processor_count ?? '—'} sub="reviewed quarterly" />
          <PostureTile icon={Lock} label="Audit collections" value={(summary?.audit_collections || []).length} sub="immutable · append-only" />
          <button onClick={downloadBrief} disabled={generating}
            className="bg-[#0B2447] hover:bg-[#19376D] text-white p-5 rounded-sm text-left transition-colors group disabled:opacity-50"
            data-testid="download-brief-btn">
            <FilePdf size={18} className="text-[#E63946]" weight="duotone" />
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/60 mt-2">Generate brief</p>
            <p className="text-sm font-bold text-white mt-0.5">
              {generating ? 'Generating…' : 'Security & Compliance Brief'}
            </p>
            <p className="text-[10px] text-white/40 mt-0.5">PDF · NDA-only</p>
          </button>
        </section>

        {/* Control checklist */}
        <section data-testid="controls-section">
          <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-[#0B2447] mb-3">SOC 2 control coverage</h2>
          <div className="bg-white border border-gray-100 rounded-sm overflow-hidden">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="bg-[#0B2447] text-white text-[10px] uppercase tracking-wider">
                  <th className="text-left px-3 py-2">ID</th>
                  <th className="text-left px-3 py-2">Control</th>
                  <th className="text-left px-3 py-2">Category</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {controls.map((c, i) => {
                  const theme = STATUS_THEME[c.status] || STATUS_THEME.planned;
                  return (
                    <tr key={c.id} className={i % 2 ? 'bg-[#F9FAFB]' : 'bg-white'} data-testid={`control-${c.id}`}>
                      <td className="px-3 py-2 font-mono text-[#4B5563]">{c.id}</td>
                      <td className="px-3 py-2 font-semibold text-[#0B2447]">{c.title}</td>
                      <td className="px-3 py-2 text-[#6B7280]">{c.category}</td>
                      <td className="px-3 py-2">
                        <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                          style={{ background: theme.bg, color: theme.fg }}>
                          {theme.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-[#4B5563]">{c.notes}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Sub-processors */}
        <section data-testid="subprocessors-section">
          <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-[#0B2447] mb-3">Sub-processor inventory</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {subProcessors.map((s, i) => (
              <div key={i} className="bg-white border border-gray-100 rounded-sm p-3" data-testid={`sp-${i}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-sm font-bold text-[#0B2447]">{s.name}</p>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-[#9CA3AF]">{s.region}</span>
                </div>
                <p className="text-[11px] text-[#6B7280] mt-0.5">{s.category}</p>
                <p className="text-[11px] text-[#374151] mt-1.5 leading-relaxed">{s.purpose}</p>
                <div className="flex gap-3 text-[10px] font-mono mt-2">
                  <span className={s.encryption_in_transit ? 'text-[#065F46]' : 'text-[#9B1C1C]'}>
                    {s.encryption_in_transit ? '✓' : '✗'} in-transit
                  </span>
                  <span className={s.encryption_at_rest ? 'text-[#065F46]' : 'text-[#9B1C1C]'}>
                    {s.encryption_at_rest ? '✓' : '✗'} at-rest
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Audit log */}
        <section data-testid="audit-log-section">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-[#0B2447]">Unified audit log</h2>
            <div className="flex items-center gap-2">
              <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
                className="border border-gray-300 rounded-sm px-2 py-1 text-xs"
                data-testid="audit-source-filter">
                <option value="">All sources</option>
                <option value="copilot">Copilot</option>
                <option value="passport">Passport</option>
                <option value="hazard">Hazard</option>
              </select>
              <button onClick={loadAudit} className="text-[#4B5563] hover:text-[#E63946] p-1" title="Refresh">
                <ArrowsClockwise size={13} weight="bold" />
              </button>
            </div>
          </div>
          <div className="bg-white border border-gray-100 rounded-sm overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="bg-[#F9FAFB] text-[10px] uppercase tracking-wider text-[#4B5563]">
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">Source</th>
                  <th className="text-left px-3 py-2">User</th>
                  <th className="text-left px-3 py-2">Action</th>
                  <th className="text-left px-3 py-2">Payload</th>
                </tr>
              </thead>
              <tbody>
                {auditRows.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-6 text-[#9CA3AF]">No audit rows yet · activity will populate as users interact</td></tr>
                ) : auditRows.map(r => (
                  <tr key={r.id} className="border-t border-gray-100" data-testid={`audit-row-${r.id}`}>
                    <td className="px-3 py-1.5 font-mono text-[#6B7280] whitespace-nowrap">{r.created_at?.slice(0, 19).replace('T', ' ')}</td>
                    <td className="px-3 py-1.5">
                      <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#0B2447] text-white">
                        {r.source}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-[#4B5563]">{r.user_email || r.user_id || '—'}</td>
                    <td className="px-3 py-1.5 font-semibold text-[#0B2447]">{r.action}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-[#6B7280] max-w-md truncate">
                      {Object.entries(r.payload || {}).map(([k, v]) => `${k}=${JSON.stringify(v).slice(0, 30)}`).join(' · ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}

function PostureTile({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-gray-100 rounded-sm p-5">
      <Icon size={18} className="text-[#E63946]" weight="duotone" />
      <p className="text-[10px] uppercase tracking-[0.25em] text-[#9CA3AF] mt-2">{label}</p>
      <p className="text-2xl font-bold text-[#0B2447] mt-0.5">{value}</p>
      {sub && <p className="text-[10px] text-[#6B7280] mt-1 font-mono">{sub}</p>}
    </div>
  );
}
